import { GenericResponse } from "../CreateRound/CreateRoundModels";
import ApiNgUtil from "../api/ApiNgUtil";
import { ListResponse } from "../api/ApiNgModels";
import InvokeApiMethods from "../ExternalApi/ExternalApiModels";
import AgentUtil from "../AgentOverview/AgentUtil";
import { Fixture, FixtureFilter } from "../TeamOverview/TeamOverviewModels";
import TeamOverviewUtil from "../TeamOverview/TeamOverviewUtil";
import { ExtFixture, ExtFixtureItem, FixtureMapping, ActionState, MapFixtureRequest, ExtFixtureFilter, BaseResponse, FixtureNg, FixtureNgItem, MapFixtureNgToFixtureRequest, FixtureOdds, MapFixtureNgRequest } from "./FixtureOverviewModels";
import CreateRoundUtil from "../CreateRound/CreateRoundUtil";
import LeagueUtil from "../LeagueOverview/LeagueUtil";

class FixtureOverviewUtil {
    static async getFixtures(pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number, filter?: FixtureFilter) : Promise<GenericResponse<ListResponse<Fixture>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, filter);
        var url = "fixtures?" + urlParams;
        var response = await ApiNgUtil.getListByUrl<Fixture>(url, "fixtures");
        if (!response.item?.items) {
            return response;
        }
        for (var item of response.item.items) {
            FixtureOverviewUtil.fixFixture(item);
        }
        return response;
    }
    static fixFixture(fixture: Fixture) {
        fixture.score = `${fixture.homeTeam?.score}-${fixture.awayTeam?.score}`;
        fixture.shirtsOrder = TeamOverviewUtil.getFixtureShirtsOrder(fixture);
    }
    static async createFixture(leagueId: string, homeTeamId: string, awayTeamId: string, startingAt: Date, name?: string) : Promise<GenericResponse<Fixture>> {
        const METHOD_NAME = "FixtureOverviewUtil.createFixture";
        var createData : any = {
            startingAt: startingAt,
            homeTeam: {
                teamId: homeTeamId
            },
            awayTeam: {
                teamId: awayTeamId
            },
            leagueId: leagueId
        };
        var fixtureName = name ? name.trim() : "";
        if (fixtureName.length > 0) {
            createData.name = fixtureName;
        }
        return await FixtureOverviewUtil.saveFixture("fixtures", InvokeApiMethods.POST(), JSON.stringify(createData), METHOD_NAME);
    }
    static async updateFixture(fixtureId: string | number, updateJson: string) : Promise<GenericResponse<Fixture>> {
        const METHOD_NAME = "FixtureOverviewUtil.updateFixture";
        return await FixtureOverviewUtil.saveFixture(`fixtures/${fixtureId}`, InvokeApiMethods.PATCH(), updateJson, METHOD_NAME);
    }
    static async saveFixture(url: string, method: string, body: string, methodName: string) : Promise<GenericResponse<Fixture>> {
        var response = await ApiNgUtil.getObjectWithBody<Fixture>(url, method, body);
        if (!response.success) {
            return response;
        }
        var fixture = response.item;
        if (!fixture) {
            return CreateRoundUtil.createGenericResponse<Fixture>(false, methodName + " failure: no fixture was returned.", null);
        }
        if (!fixture.id) {
            return CreateRoundUtil.createGenericResponse<Fixture>(false, methodName + " failure: id property is missing in response.", null);
        }
        FixtureOverviewUtil.fixFixture(fixture);
        return response;
    }
    static async getFixturesByIds(fixtureIds: number[]) : Promise<GenericResponse<Fixture[]>> {
        if (!fixtureIds || fixtureIds.length <= 0) {
            return { success: true, message: "", item: [] };
        }
        var filter : FixtureFilter = { ids: fixtureIds };
        var response = await FixtureOverviewUtil.getFixtures(fixtureIds.length, undefined, undefined, 0, filter);
        return { success: response.success, message: response.message, item: response.item?.items ?? [] };
    }
    static async getFixtureMappingsByFixture(fixtureId: number) : Promise<GenericResponse<ListResponse<FixtureMapping>>> {
        var url = `fixtures/${fixtureId}/mappings`;
        var response = await ApiNgUtil.getListByUrl<FixtureMapping>(url, "fixtureMappings");
        return response;
    }
    static async getFixtureMappingsByLeague(leagueId: number, pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number) : Promise<GenericResponse<ListResponse<FixtureMapping>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, undefined);
        var url = `leagues/${leagueId}/fixtureMappings?${urlParams}`;
        var response = await ApiNgUtil.getListByUrl<FixtureMapping>(url, "fixtureMappings");
        return response;
    }
    static async getFixtureMappings(pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number, filter?: ExtFixtureFilter) : Promise<GenericResponse<ListResponse<FixtureMapping>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, filter);
        var url = `fixtures/mappings?${urlParams}`;
        var response = await ApiNgUtil.getListByUrl<FixtureMapping>(url, "fixtureMappings");
        return response;
    }
    static async getExtFixtures(pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number | undefined, filter?: ExtFixtureFilter) : Promise<GenericResponse<ListResponse<ExtFixture>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, filter);
        var url = `extFixtures?${urlParams}`;
        var response = await ApiNgUtil.getListByUrl<ExtFixture>(url, "extFixtures");
        return response;
    }
    static async getExtFixtureItems(pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number | undefined, filter?: ExtFixtureFilter) : Promise<GenericResponse<ListResponse<ExtFixtureItem>>> {
        var resp = await FixtureOverviewUtil.getExtFixtures(pageSize, from, to, lastId, filter);
        if (!resp.success) {
            return { success: false, message: resp.message, item: null };
        }
        var listResp = resp.item;
        if (!listResp) {
            return { success: true, message: resp.message, item: null };
        }
        var items : ExtFixtureItem[] = [];
        if (listResp.items) {
            items = FixtureOverviewUtil.toExtFixtureItems(listResp.items);
        }
        var newListResp : ListResponse<ExtFixtureItem> = {
            success: listResp.success,
            message: listResp.message,
            items: items,
            page: listResp.page,
            pageSize: listResp.pageSize,
            size: listResp.size,
            totalPages: listResp.totalPages,
            totalSize: listResp.totalPages
        };
        return { success: true, message: resp.message, item: newListResp };
    }

    static toExtFixtureItem(extFixture: ExtFixture) : ExtFixtureItem {
        var item : ExtFixtureItem = {
            extFixture: extFixture,
            id: extFixture.id,
            providerId: extFixture.providerId,
            extId: extFixture.extId,
            name: extFixture.name,
            startingAt: extFixture.startingAt,
            // extStatus: extFixture.extStatus,
            // status: extFixture.status,
            competitionId: extFixture.competitionId,
            idPath: extFixture.idPath,
            namePath: extFixture.namePath
        };
        var mapping = extFixture.mapping;
        if (mapping) {
            item.mappingId = mapping.id;
            item.fixtureId = mapping.fixtureId;
            item.fixtureName = mapping.fixtureName;
            item.fixtureStartingAt = mapping.startingAt;
            item.leagueId = mapping.leagueId;
            item.leagueName = mapping.leagueName;
            if (mapping.fixtureId && mapping.fixtureId > 0) {
                item.fixtureStatus = mapping.fixtureStatus;
                item.mappingStatus = mapping.status;
            }
            item.mappingReversed = mapping.reversed;
            var leagueNameParts = LeagueUtil.getLeagueNameParts(mapping.leagueName);
            item.leagueTitle = (leagueNameParts) ? leagueNameParts.title : mapping.leagueName;
        }
        var fng = extFixture.fixtureNg;
        if (fng) {
            item.fixtureNgId = fng.id;
            item.fixtureNgName = fng.name;
            item.fixtureNgStartingAt = fng.startingAt;
            item.competitionNgId = fng.competitionId;
            item.leagueName = mapping.leagueName;
            var cng = fng.competition;
            if (cng) {
                item.competitionNgName = cng.name;
                item.sportNgId = cng.sportId;
                item.sportNgName = cng.sportName;
                item.countryNgId = cng.countryId;
                item.countryNgName = cng.countryName;
            }
        }
        return item;
    }
    static toExtFixtureItems(extFixtures: ExtFixture[]) : ExtFixtureItem[] {
        if (!extFixtures) {
            return [];
        }
        var items : ExtFixtureItem[] = [];
        for (const extFixture of extFixtures) {
            var item = FixtureOverviewUtil.toExtFixtureItem(extFixture);
            items.push(item);
        }
        return items;
    }
    static async mapExtFixture(request: MapFixtureRequest) : Promise<GenericResponse<ExtFixture>> {
        const METHOD_NAME = "FixtureOverviewUtil.mapExtFixture";
        var url = `fixtures/${request.fixtureId}/map`;
        var saveJson = JSON.stringify(request);
        try {
            var resp = await ApiNgUtil.getObjectWithBody<ExtFixture>(url, InvokeApiMethods.POST(), saveJson);
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<ExtFixture>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async unmapFixture(mappingId: number) : Promise<GenericResponse<ExtFixture>> {
        const METHOD_NAME = "FixtureOverviewUtil.unmapFixture";
        var url = `fixtures/mappings/${mappingId}`;
        try {
            var resp = await ApiNgUtil.getObjectWithMethod<ExtFixture>(url, InvokeApiMethods.DELETE());
            return resp;
            // if (resp.success) {
            //     if (resp.item) {
            //         const success : boolean = (resp.item.success && resp.item.success === true);
            //         return CreateRoundUtil.createGenericResponse(success, resp.item.message, success);
            //     } 
            //     return CreateRoundUtil.createGenericResponse(false, "No response", false);
            // }
            // return CreateRoundUtil.createGenericResponse(false, resp.message, false);
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<ExtFixture>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async getFixtureNgs(pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number | undefined, filter?: ExtFixtureFilter) : Promise<GenericResponse<ListResponse<FixtureNg>>> {
        var urlParams = AgentUtil.getUrlParams(pageSize, from, to, lastId, filter);
        var url = `fixtureNgs?${urlParams}`;
        var response = await ApiNgUtil.getListByUrl<FixtureNg>(url, "fixtureNgs");
        return response;
    }
    static async getFixtureNgItems(pageSize: number, from: Date| undefined, to: Date| undefined, lastId: number | undefined, filter?: ExtFixtureFilter) : Promise<GenericResponse<ListResponse<FixtureNgItem>>> {
        var resp = await FixtureOverviewUtil.getFixtureNgs(pageSize, from, to, lastId, filter);
        if (!resp.success) {
            return { success: false, message: resp.message, item: null };
        }
        var listResp = resp.item;
        if (!listResp) {
            return { success: true, message: resp.message, item: null };
        }
        var items : FixtureNgItem[] = [];
        if (listResp.items) {
            items = FixtureOverviewUtil.toFixtureNgItems(listResp.items);
        }
        var newListResp : ListResponse<FixtureNgItem> = {
            success: listResp.success,
            message: listResp.message,
            items: items,
            page: listResp.page,
            pageSize: listResp.pageSize,
            size: listResp.size,
            totalPages: listResp.totalPages,
            totalSize: listResp.totalPages
        };
        return { success: true, message: resp.message, item: newListResp };
    }
    static toFixtureNgItem(fixtureNg: FixtureNg) : FixtureNgItem {
        var item : FixtureNgItem = {
            item: fixtureNg,
            id: fixtureNg.id,
            name: fixtureNg.name,
            fixtureId: fixtureNg.fixtureId,
            competitionId: fixtureNg.competitionId,
            startingAt: fixtureNg.startingAt,
            status: fixtureNg.status,
            mappingCount: fixtureNg.mappingCount
        };
        var fixture = fixtureNg.fixture;
        if (fixture) {
            item.fixtureName = fixture.name;
            item.fixtureStartingAt = fixture.startingAt;
            item.fixtureStatus = fixture.status;
            item.leagueName = fixture.leagueName;
            item.leagueTitle = fixture.leagueTitle;
        }
        var competition = fixtureNg.competition;
        if (competition) {
            item.competitionName = competition.name;
            item.sportName = competition.sportName;
            item.countryName = competition.countryName;
        }
        return item;
    }
    static toFixtureNgItems(fixtureNgs: FixtureNg[]) : FixtureNgItem[] {
        if (!fixtureNgs) {
            return [];
        }
        var items : FixtureNgItem[] = [];
        for (const fixtureNg of fixtureNgs) {
            var item = FixtureOverviewUtil.toFixtureNgItem(fixtureNg);
            items.push(item);
        }
        return items;
    }
    static async mapFixtureNgFixture(request: MapFixtureNgToFixtureRequest) : Promise<GenericResponse<FixtureNg>> {
        const METHOD_NAME = "FixtureOverviewUtil.mapFixtureNgFixture";
        var url = `fixtureNgs/${request.fixtureNgId}/mapFixture`;
        var saveJson = JSON.stringify(request);
        try {
            var resp = await ApiNgUtil.getObjectWithBody<FixtureNg>(url, InvokeApiMethods.POST(), saveJson);
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<FixtureNg>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async unmapFixtureNgFixture(fixtureNgId: number) : Promise<GenericResponse<FixtureNg>> {
        const METHOD_NAME = "FixtureOverviewUtil.unmapFixtureNgFixture";
        var url = `fixtureNgs/${fixtureNgId}/unmapFixture`;
        try {
            var resp = await ApiNgUtil.getObjectWithMethod<FixtureNg>(url, InvokeApiMethods.DELETE());
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<FixtureNg>(false, METHOD_NAME + " error: " + error, null);
        }
    }

    static async mapExtFixtureToFixtureNg(request: MapFixtureNgRequest) : Promise<GenericResponse<ExtFixture>> {
        const METHOD_NAME = "FixtureOverviewUtil.mapExtFixtureToFixtureNg";
        var url = `fixtureNgs/${request.fixtureNgId}/map`;
        var saveJson = JSON.stringify(request);
        try {
            var resp = await ApiNgUtil.getObjectWithBody<ExtFixture>(url, InvokeApiMethods.POST(), saveJson);
            return resp;
        } catch (error) {
            console.error(METHOD_NAME + " error.", error);
            return CreateRoundUtil.createGenericResponse<ExtFixture>(false, METHOD_NAME + " error: " + error, null);
        }
    }
    static async getFixtureOdds(fixtureId: number, operatorId: string) : Promise<GenericResponse<FixtureOdds[]>> {
        var filter = { fixtureId: fixtureId, operatorId: operatorId};
        var urlParams = AgentUtil.getUrlParams(100, undefined, undefined, undefined, filter);
//        var url = `fixtures/${fixtureId}/odds?${urlParams}`;
        var url = `fixtures/${fixtureId}/odds?operatorId=${encodeURI(operatorId)}`;
        var response = await ApiNgUtil.getObject<FixtureOdds[]>(url);
        return response;
    }

    static createEmptyActionState() : ActionState {
        return { executing: false, error: false, message: "" };
    }
    static createActionState(running: boolean, error: boolean, message: string) : ActionState {
        return { executing: running, error: error, message: message };
    }
}
export default FixtureOverviewUtil;